import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import Sidebar from '../../Components/Sidebar/Sidebar';
import { FaHome, FaPlusSquare, FaUser } from 'react-icons/fa';
import { Explore, Mail, AccountCircle } from '@mui/icons-material';
import { User, X, Search, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_URL;

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [query]);

  return matches;
}

export default function Layout() {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  const { t } = useTranslation('Sidebardash');

  const debouncedSearch = useCallback((query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `${API_BASE}/properties/?search=${encodeURIComponent(query)}`,
          { credentials: 'include' }
        );
        if (!response.ok) {
          throw new Error(`Search HTTP ${response.status}`);
        }
        const data = await response.json();
        setSearchResults(data.results || data);
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE}/properties/search-suggestions/?q=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw new Error(`Suggestions HTTP ${res.status}`);
      }
      const data = await res.json();
      setSuggestions(data);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery);
      fetchSuggestions(searchQuery);
    } else {
      setSearchResults([]);
      setSuggestions([]);
    }
  }, [searchQuery, debouncedSearch, fetchSuggestions]);

  useEffect(() => {
    // Preload CSRF cookie
    fetch(`${API_BASE}/get-csrf/`, {
      credentials: 'include',
    }).catch((err) => {
      console.error('Failed to preload CSRF:', err);
    });
  }, []);

  return (
    <>
      {/* Search Panel for Public Users */}
      {isSearchPanelOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setIsSearchPanelOpen(false)}
          />
          <div
            className="fixed top-0 left-0 z-50 w-full max-w-xs sm:w-80 h-screen bg-[var(--bg-black)] border-r border-[var(--border)] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[var(--border)]">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-[var(--text-black)]">
                  {t('sidebar.search.resultsTitle')}
                </h3>
                <button
                  onClick={() => setIsSearchPanelOpen(false)}
                  className="text-[var(--text-black)] hover:text-pink-500 bg-transparent border-none"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mt-4 relative">
                <Search
                  className="absolute left-3 top-1.5 -translate-y-1/2"
                  size={18}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search properties..."
                  className="w-full pl-10 pr-4 py-2 bg-[var(--bg-black)] border border-[var(--border)] rounded-lg text-[var(--text-black)]"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1.5"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>

            {suggestions.length > 0 && searchQuery ? (
              <div className="p-4">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => {}}>
                    {s}
                  </button>
                ))}
              </div>
            ) : searchResults.length > 0 ? (
              <div className="p-4">
                {searchResults.slice(0, 5).map((result) => (
                  <div
                    key={result.id}
                    onClick={() => {
                      setIsSearchPanelOpen(false);
                      navigate(`/property/${result.id}`);
                    }}
                  >
                    {result.title}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </>
      )}

      {/* Mobile Menu Popup */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed bottom-16 left-1/2 transform -translate-x-1/2 z-40 bg-[var(--bg-black)] border border-[var(--border)] rounded-xl shadow-lg overflow-hidden min-w-[180px]">
            {[
              { name: 'Messages', path: '/login', icon: <Mail /> },
              { name: 'Profile', path: '/login', icon: <AccountCircle /> },
              { name: 'About', path: '/about', icon: <Users/> },
              { name: 'Search', action: 'search', icon: <Search size={20} /> },
              { name: 'Contact', path: '/login', icon: <User size={20} /> },
              { name: 'Login', path: '/login', icon: <FaUser size={16} /> },
            ].map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  setMobileMenuOpen(false);
                  if (item.action === 'search') {
                    setIsSearchPanelOpen(true);
                    setSearchQuery('');
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
                className="w-full text-left px-4 py-3 bg-[var(--bg-black)] text-[var(--text-black)] flex items-center gap-3 text-sm font-medium border-white/10 border-solid"
              >
                <span className="text-lg">{item.icon}</span>
                {item.name !== 'Search' && <span>{item.name}</span>}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Main layout */}
      <div className="flex min-h-screen bg-[var(--bg-black)] text-[var(--text-black)] relative">
        {isDesktop && <Sidebar />}

        <main
          className={`flex-1 transition-all duration-300 ${
            isDesktop ? 'ml-16 lg:ml-20 xl:ml-24' : ''
          }`}
        >
          <Outlet />
        </main>

        {!isDesktop && (
          <div className="fixed bottom-0 left-0 right-0 bg-[var(--bg-black)] backdrop-blur-xl px-4 py-2 z-30">
            <div className="flex justify-around items-center text-[var(--text-black)]">
              {[
                { to: '/', icon: <FaHome size={16} />, label: 'Home' },
                { to: '/explore', icon: <Explore size={16} />, label: 'Explore' },
                { to: '/login', icon: <FaPlusSquare size={16} />, label: 'Upload' },
              ].map((item, idx) => {
                const isActive = location.pathname === item.to;
                return (
                  <button
                    key={idx}
                    onClick={() => navigate(item.to)}
                    className="flex flex-col items-center py-1 px-1 bg-transparent border-none"
                  >
                    <span
                      className={
                        isActive ? 'text-pink-500' : 'text-[var(--text-black)]'
                      }
                    >
                      {item.icon}
                    </span>
                    <span
                      className={`text-xs mt-1 ${
                        isActive
                          ? 'text-pink-500 font-medium'
                          : 'text-[var(--text-black)]'
                      }`}
                    >
                      {item.label}
                    </span>
                  </button>
                );
              })}

              <button
                onClick={() => setMobileMenuOpen(true)}
                className="flex flex-col items-center py-1 px-1 bg-transparent border-none"
              >
                <div className="text-lg text-[var(--text-black)] font-bold">≡</div>
                <span className="text-xs mt-1 text-[var(--text-black)]">Menu</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
  